import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import LogoSvg from '@/components/ui/LogoSvg';
import { PinOff, Pin, User, Home, CreditCard, CircleUser } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/context/Auth';
import { useNavigate } from 'react-router-dom';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import DarkThemeToggle from '../ui/dark-theme-toggle';
import { useDispatch } from 'react-redux';
import { fetchAgents } from '@/redux/agentsSlice';
import { AppDispatch } from '@/redux/store';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import NewAgent from '@/components/page-components/project/agents/NewAgent';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import NotificationBell from './notifications/NotificationBell';
import HelpMenu from './HelpMenu';
import { ACCOUNT_ROUTE } from '@/utils/appRoutes';

interface NavItemProps {
  to?: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  isExpanded: boolean;
  onClick?: () => void;
  hide?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const NavItem: React.FC<NavItemProps> = ({
  to,
  icon,
  label,
  isActive,
  isExpanded,
  onClick,
  hide = false,
  className,
  style,
}) => {
  if (hide) return null;

  const buttonContent = (
    <div
      className={`flex flex-row items-center justify-start ${className || ''}`}
      style={style}
    >
      <Button
        variant="ghost"
        data-active={isActive}
        className="w-full justify-start hover:!bg-transparent hover:text-foreground dark:hover:text-foreground transition-colors duration-150 outline-none data-[active=true]:text-brand dark:data-[active=true]:text-brand data-[active=true]:font-semibold data-[active=false]:text-muted-foreground dark:data-[active=false]:text-muted-foreground data-[active=false]:hover:text-foreground dark:data-[active=false]:hover:text-foreground"
        onClick={onClick}
      >
        {icon}
        <span
          className={`ml-2 transition-[opacity,transform] duration-300 ease-in-out text-lg truncate max-w-[180px] ${
            isExpanded
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'
          }`}
        >
          {label}
        </span>
      </Button>
    </div>
  );

  if (to) {
    return <Link to={to}>{buttonContent}</Link>;
  }

  return buttonContent;
};
const UserPopover: React.FC<{
  user: {
    user_metadata: {
      avatar_url?: string;
      full_name?: string;
      email?: string;
    };
  };
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSignOut: () => void;
  isExpanded: boolean;
}> = ({ user, isOpen, onOpenChange, onSignOut, isExpanded }) => {
  const { t } = useTranslation();

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <div className="flex flex-row items-center gap-2 w-full">
          <Avatar className="w-10 h-10">
            <AvatarImage
              src={user?.user_metadata.avatar_url}
              alt={
                user?.user_metadata.full_name ||
                t('sidebar.user.unnamed.translation')
              }
            />
            <AvatarFallback>
              <User className="h-5 w-5" />
            </AvatarFallback>
          </Avatar>
          <div
            className={`flex flex-col p-2 rounded-lg transition-all duration-300  ${
              isExpanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            <span className="text-sm font-semibold text-foreground">
              {user?.user_metadata.full_name}
            </span>
            <span className="text-xs text-muted-foreground">
              {user?.user_metadata.email}
            </span>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-52" side="top" sideOffset={10} align="start">
        <div className="flex flex-col gap-3">
          <div className="flex flex-row px-4">
            <DarkThemeToggle />
          </div>
          <div className="flex flex-row">
            <Button variant="link" onClick={onSignOut}>
              {t('sidebar.user.logout.translation')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Sidebar: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [showNewAgentForm, setShowNewAgentForm] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    dispatch(fetchAgents());
  }, [dispatch]);

  const handleNotificationsOpenChange = (open: boolean) => {
    setIsNotificationsOpen(open);
    if (open || isLocked || isMobile) return;
    // On close the hover handlers take over again, but onMouseLeave has
    // already fired and will not fire a second time. Settle the rail against
    // where the pointer actually is now.
    setIsExpanded(sidebarRef.current?.matches(':hover') ?? false);
  };

  const handleSignOut = async () => {
    await signOut();
    setIsPopoverOpen(false);
    navigate('/login');
  };

  const handleAgentCreated = () => {
    setShowNewAgentForm(false);
    // Refresh agents list
    dispatch(fetchAgents());
  };

  // Handle navigation for mobile
  const handleNavigation = (to: string) => {
    if (isMobile && onClose) {
      onClose();
    }
    navigate(to);
  };

  return (
    <div
      ref={sidebarRef}
      className={`h-full transition-all duration-300 overflow-clip ${
        isMobile ? 'w-64 bg-transparent' : 'bg-background'
      } ${isMobile ? '' : isExpanded ? 'w-64' : 'w-16'} flex flex-col`}
      onMouseEnter={() => {
        if (!isLocked && !isMobile) setIsExpanded(true);
      }}
      onMouseLeave={() => {
        // The notifications panel is anchored to the rail's right edge, so
        // collapsing underneath it would strand it 12rem from the bell.
        if (!isLocked && !isMobile && !isNotificationsOpen)
          setIsExpanded(false);
      }}
    >
      <div className="px-4 py-3 flex flex-row items-center justify-between">
        <Link
          to="/"
          className="flex flex-row items-center gap-2"
          onClick={isMobile ? onClose : undefined}
        >
          <LogoSvg className="flex flex-row items-center justify-center h-7 w-7 m-1" />
          <h1
            className={`text-xl font-bold font-company text-foreground transition-all duration-100 ${
              isExpanded || isMobile ? 'opacity-100' : 'opacity-0'
            }`}
          >
            dialup
          </h1>
        </Link>
      </div>
      <div className="flex-1 flex items-center">
        <div className="flex flex-col justify-center gap-5">
          <NavItem
            to="/"
            icon={<Home className="h-7 w-7" />}
            label={t('common.home')}
            isActive={location.pathname === '/home'}
            isExpanded={isExpanded || isMobile}
            onClick={isMobile ? () => handleNavigation('/home') : undefined}
          />
          {/* Not a NavItem: it opens a popover rather than navigating, but it
              matches their sizing so the collapsed rail stays aligned. */}
          <div className="flex flex-row items-center justify-start">
            <NotificationBell
              variant="sidebar"
              isExpanded={isExpanded || isMobile}
              onOpenChange={handleNotificationsOpenChange}
              onNavigate={isMobile ? onClose : undefined}
            />
          </div>
          <NavItem
            icon={<CreditCard className="h-7 w-7" />}
            label={t('sidebar.billing.translation')}
            isActive={location.pathname === '/billing'}
            isExpanded={isExpanded || isMobile}
            to="/billing"
            onClick={isMobile ? () => handleNavigation('/billing') : undefined}
          />
          <NavItem
            icon={<CircleUser className="h-7 w-7" />}
            label={t('sidebar.account.translation', 'Account')}
            isActive={location.pathname === ACCOUNT_ROUTE}
            isExpanded={isExpanded || isMobile}
            to={ACCOUNT_ROUTE}
            onClick={
              isMobile ? () => handleNavigation(ACCOUNT_ROUTE) : undefined
            }
          />
          {/* Not a NavItem either: help is now a choice between the public
              documentation site and the in-app tools, so it opens a popover. */}
          <HelpMenu
            isExpanded={isExpanded || isMobile}
            onNavigate={isMobile ? onClose : undefined}
          />
        </div>
      </div>

      {/* NewAgent Dialog */}
      <Dialog open={showNewAgentForm} onOpenChange={setShowNewAgentForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sidebar.new_agent.translation')}</DialogTitle>
            <DialogDescription>
              {t('agent.new.description.translation')}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-full">
            <NewAgent onAgentCreated={handleAgentCreated} />
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <div className="flex flex-row w-full p-2 items-center justify-between">
        <div className="flex flex-col p-1 cursor-pointer">
          {user && (
            <UserPopover
              user={user}
              isOpen={isPopoverOpen}
              onOpenChange={setIsPopoverOpen}
              onSignOut={handleSignOut}
              isExpanded={isExpanded || isMobile}
            />
          )}
        </div>

        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsLocked(prev => !prev)}
            className={`transition-opacity duration-100 ${
              isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            {isLocked ? (
              <PinOff className="h-4 w-4" />
            ) : (
              <Pin className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
